// Device-local "remember me" profile — no accounts, no server storage.
// Lives entirely in localStorage on this browser/device: a nickname for the
// greeting, plus the sender details (name/address/contact) so returning
// senders don't retype them. Never sent anywhere except back into this
// app's own forms as pre-filled defaults the sender can still edit.

export interface DeviceSenderProfile {
  name?: string;
  postcode?: string;
  addressLine1?: string;
  addressLine2?: string;
  townCity?: string;
  email?: string;
  mobile?: string;
}

export interface DeviceProfile {
  nickname: string;
  sender?: DeviceSenderProfile;
}

const STORAGE_KEY = "smp_device_profile_v1";

export function getDeviceProfile(): DeviceProfile | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.nickname !== "string" || !parsed.nickname) return null;
    return parsed as DeviceProfile;
  } catch {
    return null;
  }
}

export function saveNickname(nickname: string): void {
  const existing = getDeviceProfile();
  writeProfile({ ...existing, nickname });
}

export function saveDeviceSender(sender: DeviceSenderProfile): void {
  const existing = getDeviceProfile();
  if (!existing) return;
  writeProfile({ ...existing, sender });
}

/** Saves the editable profile shown in Settings, including clearing an old address. */
export function saveDeviceProfile(profile: DeviceProfile): void {
  writeProfile(profile);
}

/** Overwrites the whole profile at once — used when a sync code from another device is redeemed. */
export function applySyncedProfile(profile: DeviceProfile): void {
  writeProfile(profile);
}

function writeProfile(profile: DeviceProfile): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
  } catch {
    // Private browsing / storage disabled — degrade silently, we just won't remember.
  }
}
